import React, { useState } from 'react';
import { rotuloDoMes } from './SeletorDeMes';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import './ValorDoMes.css';

/**
 * Confirmar o mês de um lançamento — o valor de uma mensal, ou a fatura do
 * cartão (15/09).
 *
 * ── O pedido ────────────────────────────────────────────────────────────
 *
 * "assim que eu pagar/receber naquele mês eu posso informar se teve ou não
 * variação de valor aquele mês pra atualizar com o valor correto pago." E, para
 * o cartão: as compras ficam aguardando "até eu informar que o cartão foi pago".
 *
 * ── Dois modos, uma janela ──────────────────────────────────────────────
 *
 * `mensal`: "Paguei R$ X (mesmo valor)", ou "outro valor". Informar é também
 * confirmar. Com valor já informado, dá para corrigir ou desfazer.
 *
 * `fatura`: a compra está num cartão com dia de pagamento, e quem paga é a
 * fatura — inclusive uma mensal no cartão. Não pede valor (decisão dela): o
 * total é a soma do que está lançado.
 *
 * Janela e não popover: a linha da lista é estreita no celular, e o teclado
 * numérico precisa de espaço.
 */

export type ModoDoValorDoMes =
  | { tipo: 'mensal'; descricao: string; receita: boolean; previsto: number; informado: number | null }
  | { tipo: 'fatura'; cartao: string; paga: boolean };

interface Props {
  modo: ModoDoValorDoMes;
  mes: string;
  fmt: (v: number) => string;
  onInformar: (valor: number) => Promise<void>;
  onDesfazer: () => Promise<void>;
  onFatura: (paga: boolean) => Promise<void>;
  onFechar: () => void;
}

/** "18750" → "187,50". A mesma máscara do formulário de lançamento. */
export const mascara = (raw: string): string => {
  const digitos = raw.replace(/\D/g, '');
  if (!digitos) return '';
  return (parseInt(digitos, 10) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const numero = (mascarado: string): number => parseFloat(mascarado.replace(/\./g, '').replace(',', '.'));

const ValorDoMes: React.FC<Props> = ({ modo, mes, fmt, onInformar, onDesfazer, onFatura, onFechar }) => {
  const [valor, setValor] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const mesTexto = rotuloDoMes(mes);

  // Falhou: a janela fica aberta e DIZ — fechar calada faria parecer gravado.
  const agir = async (acao: () => Promise<void>) => {
    setOcupado(true);
    setErro('');
    try {
      await acao();
      onFechar();
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui gravar. Tente de novo.'));
      setOcupado(false);
    }
  };

  const valorOutro = numero(valor);
  const outroValido = Number.isFinite(valorOutro) && valorOutro >= 0 && valor !== '';

  return (
    <div className="vdm-overlay" onClick={onFechar}>
      <div
        className="vdm-janela"
        role="dialog"
        aria-modal="true"
        aria-label={modo.tipo === 'fatura' ? `Fatura do ${modo.cartao}` : `Valor de ${modo.descricao}`}
        onClick={e => e.stopPropagation()}
      >
        {modo.tipo === 'fatura' ? (
          <>
            <h3 className="vdm-titulo">Fatura do {modo.cartao} · {mesTexto}</h3>
            <p className="vdm-texto">
              {modo.paga
                ? 'A fatura deste mês está marcada como paga, e as compras dela já contam no saldo.'
                : 'As compras deste cartão ficam aguardando até a fatura do mês ser paga.'}
            </p>
            <div className="vdm-acoes">
              {modo.paga ? (
                <button type="button" className="vdm-btn" disabled={ocupado} onClick={() => agir(() => onFatura(false))}>
                  Desmarcar fatura paga
                </button>
              ) : (
                <button type="button" className="vdm-btn vdm-btn-principal" disabled={ocupado} onClick={() => agir(() => onFatura(true))}>
                  Fatura de {mesTexto} paga ✓
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <h3 className="vdm-titulo">{modo.descricao} · {mesTexto}</h3>
            <p className="vdm-texto">
              {modo.informado !== null
                ? `Informado: ${fmt(modo.informado)}.`
                : `${modo.receita ? 'Recebeu' : 'Pagou'}? Se o valor mudou este mês, informe o que ${modo.receita ? 'entrou' : 'saiu'} de verdade.`}
            </p>
            <div className="vdm-acoes">
              {modo.informado === null && (
                <button type="button" className="vdm-btn vdm-btn-principal" disabled={ocupado} onClick={() => agir(() => onInformar(modo.previsto))}>
                  {modo.receita ? 'Recebi' : 'Paguei'} {fmt(modo.previsto)} (mesmo valor)
                </button>
              )}
              <div className="vdm-outro">
                <label htmlFor="vdm-outro-valor">{modo.informado !== null ? 'Corrigir para' : 'Outro valor'}</label>
                <div className="vdm-outro-linha">
                  <span className="vdm-rs">R$</span>
                  <input
                    id="vdm-outro-valor"
                    name="outroValor"
                    type="text"
                    inputMode="numeric"
                    placeholder="0,00"
                    value={valor}
                    onChange={e => setValor(mascara(e.target.value))}
                  />
                  <button
                    type="button"
                    className="vdm-btn vdm-btn-salvar"
                    disabled={ocupado || !outroValido}
                    onClick={() => agir(() => onInformar(valorOutro))}
                  >
                    Salvar
                  </button>
                </div>
              </div>
              {modo.informado !== null && (
                <button type="button" className="vdm-btn vdm-btn-desfazer" disabled={ocupado} onClick={() => agir(onDesfazer)}>
                  Desfazer e voltar ao previsto
                </button>
              )}
            </div>
          </>
        )}
        {erro && <p className="vdm-erro" role="alert">{erro}</p>}
        <button type="button" className="vdm-cancelar" onClick={onFechar} disabled={ocupado}>Fechar</button>
      </div>
    </div>
  );
};

export default ValorDoMes;
